import type { ProjectStatus, TaskStatus } from './dto';

/**
 * المصنع الوحيد لمفاتيح الاستعلام (frontend-design §1.3). أي مفتاح يُكتب حرفيًا
 * في مكوّن يخرج عن نطاق إبطال SSE بصمت — فلا مفاتيح خارج هذا الملف.
 */

export interface ProjectFilters {
  readonly venture_id?: string;
  readonly status?: readonly ProjectStatus[];
  readonly stalled?: boolean;
  readonly q?: string;
}

export interface TaskFilters {
  readonly project_id?: string | 'null';
  readonly status?: readonly TaskStatus[];
  readonly q?: string;
}

export const qk = {
  agendaToday: () => ['agenda', 'today'] as const,
  projects: (f?: ProjectFilters) => ['projects', 'list', f ?? {}] as const,
  project: (id: string) => ['projects', 'detail', id] as const,
  /** قائمة خفيفة لمنتقي المشروع؛ لا تشارك حمولة `InfiniteData` مع قائمة المشاريع. */
  projectPicker: () => ['projects', 'picker'] as const,
  /** أقسام المشروع (المحاور) — تحت بادئة `['projects']` فتشملها إبطالات المشروع. */
  projectAxes: (id: string) => ['projects', 'axes', id] as const,
  projectStatus: (id: string) => ['projects', 'status', id] as const,
  projectTimeline: (id: string) => ['projects', 'timeline', id] as const,
  projectMemory: (id: string) => ['projects', 'memory', id] as const,
  projectSources: (id: string) => ['projects', 'sources', id] as const,
  entries: (pt: string, pid: string) => ['entries', pt, pid] as const,
  tasks: (f?: TaskFilters) => ['tasks', 'list', f ?? {}] as const,
  task: (id: string) => ['tasks', 'detail', id] as const,
  captures: (triage: string) => ['captures', triage] as const,
  /** التقاطة واحدة بمعرّفها — عنوان هدف التذكير (§6.9) يُقرأ من الكيان نفسه. */
  capture: (id: string) => ['captures', 'detail', id] as const,
  search: (q: string) => ['search', q] as const,
  reminders: (statuses?: readonly string[]) => ['reminders', statuses ?? []] as const,
  /**
   * قائمة شاشة «التذكيرات» المرقَّمة بالمؤشر (§6.9). مفتاح مستقل عن `reminders()`
   * عن قصد: ذاك مجموعٌ واحد غير مرقَّم (`Page`) تقرأه شاشة «اليوم»، وهذا
   * `InfiniteData<Page>` — حمولتان مختلفتا الشكل على مفتاح واحد تعطّل القارئ
   * الآخر بصمت. والبادئة `['reminders']` مشتركة فيبطلهما إبطال واحد.
   */
  remindersList: (scope: 'active' | 'done') => ['reminders', 'list', scope] as const,
  agentJobs: () => ['agent-jobs'] as const,
  agentJob: (id: string) => ['agent-jobs', 'detail', id] as const,
  agentJobResults: (id: string) => ['agent-jobs', 'results', id] as const,
  agentTrace: (id: string) => ['agent-traces', id] as const,
  decisions: (projectId: string) => ['decisions', projectId] as const,
  /** نقاط تقدم مشروع — قائمة واحدة بلا ترقيم (الترتيب اليدوي لا يقبل مؤشر id). */
  projectMilestones: (projectId: string) => ['milestones', projectId] as const,
  /**
   * مكتبة هيرمز تحت بادئة `['library']` واحدة: المهارة والشخصية تتحركان معًا عند
   * المزامنة، فإبطال واحد يغطيهما ولا يبقى نصفا الشاشة على حالتين متناقضتين.
   */
  projectSkills: (projectId: string) => ['library', 'skills', projectId] as const,
  projectPersonas: (projectId: string) => ['library', 'personas', projectId] as const,
  hermesSyncStatus: () => ['hermes', 'sync-status'] as const,
  /**
   * فهرس المكتبة كلها (بلا نطاق مشروع) — يقرؤه منتقي «اربط مهارة قائمة» وحده.
   * تحت بادئة `['library']` كي يبطله `invalidateLibraryScope` القائم بلا تعديل.
   */
  skillsCatalog: () => ['library', 'skills', 'catalog'] as const,
  /** نماذج ورشة الكتابة واختيار المالك — يُبطَل عند تبديل النموذج. */
  workshopModels: () => ['workshop', 'models'] as const,
  /**
   * الـ prompt المُركَّب لشخصية في سياق مشروع (hermes-loop §5.2). تحت `['library']`
   * عمدًا: تعديل أي مهارة مرتبطة يغيّر التركيب وبصمته، فإبطال المكتبة يشمله —
   * عارضٌ يعرض بصمة بائتة بعد تعديل مهارة يكذب في الشيء الوحيد الذي وُجد ليقوله.
   */
  personaPrompt: (personaId: string, projectId: string | null) =>
    ['library', 'persona-prompt', personaId, projectId] as const,
  /**
   * تذكيرات مشروع واحد — تحت بادئة `['reminders']` عمدًا كي يغطيها إبطال
   * `invalidateRemindersScope` القائم بلا تعديل عليه.
   */
  projectReminders: (projectId: string) => ['reminders', 'project', projectId] as const,
  /** صندوق الأفكار الشخصي — بادئته الخاصة، فلا يشمله إبطال المشاريع. */
  ideas: (f?: { status?: string; category?: string }) => ['ideas', 'list', f ?? {}] as const,
  /* قسم الإنجليزية — والمصنع هو المدخل الوحيد، ومفتاحٌ يدويّ يخرج من إبطال SSE صامتًا. */
  englishTexts: (f?: { topic?: string; level?: string; status?: string }) =>
    ['english', 'texts', f ?? {}] as const,
  englishText: (id: string) => ['english', 'text', id] as const,
  englishWords: (f?: { scope?: string; textId?: string }) => ['english', 'words', f ?? {}] as const,
  journalEntries: (f?: { q?: string; entryType?: string; isDraft?: boolean }) =>
    ['journal', 'entries', f ?? {}] as const,
  quickNotes: () => ['journal', 'notes'] as const,
  /** القصائد — قسمٌ مستقلّ عن المفكرة (‏2026-10-20). */
  poems: (f?: { q?: string; draftsOnly?: boolean }) => ['poems', 'list', f ?? {}] as const,
  publishingPosts: (f?: { q?: string; stage?: string; platform?: string }) =>
    ['publishing', 'posts', f ?? {}] as const,
  /** لمحة الرئيسية — عدّاداتٌ من `/views/personal`. */
  personalGlance: () => ['views', 'personal'] as const,
  archiveItems: (f?: { category?: string; literaryKind?: string; q?: string }) =>
    ['archive', 'items', f ?? {}] as const,
  archiveTags: (category?: string) => ['archive', 'tags', category ?? ''] as const,
  /*
   * **اسمٌ مختلف عن `skillsCatalog` أعلاه عمدًا**: تلك كتالوج مكتبة المالك
   * (`library/skills/catalog`)، وهذه كتالوج skills.sh الخارجيّ. وأول صياغةٍ
   * سمّتهما بالاسم نفسه فأمسكها المُصرِّف — ولولاه لتشاركتا كاشًا واحدًا.
   */
  skillsShCatalog: () => ['skills-catalog'] as const,
  /**
   * سياق وكيل القسم (د1) — بادئة `projects` كي يُبطله تغيّر الشخصية أو
   * المهارات أو الذاكرة، وكلها تحت هذا الجذر.
   */
  axisContext: (axisId: string) => ['projects', 'axis-context', axisId] as const,
  /** محادثة القسم (ج1) — البادئة `conversation` تطابق عائلة SSE. */
  axisConversation: (axisId: string) => ['conversation', 'axis', axisId] as const,
  news: () => ['news'] as const,
  newsFeeds: () => ['news', 'feeds'] as const,
  /**
   * إعدادات الأنظمة المستقلّة (`/feature-config/:feature`) — **بجذرٍ خاصّ بها**
   * لا تحت `settings`: إبطالُ إعدادات فلو لا يُبطلها والعكس، وهو أثرُ فصلها
   * الذي أمر به المالك 2026-08-04 على مستوى ذاكرة الويب أيضًا.
   */
  featureConfig: (feature: string) => ['feature-config', feature] as const,
  ventures: () => ['ventures'] as const,
  /** حالة الجلسة الحالية (`GET /auth/session`) — تقرأها شاشة الدخول. */
  authSession: () => ['auth', 'session'] as const,
  webSessions: () => ['web-sessions'] as const,
  settings: () => ['settings'] as const,
  /**
   * حالة طبقة الذكاء (`GET /ai/status`) — **جذرٌ خاصّ لا فرعٌ تحت `settings`**:
   * محتواه مشتقٌّ من الإعدادات **ومن البيئة ومن حالة القاطع** معًا، فليس نسخة
   * أخرى منها. وكل كتابة إعداد تُبطله صراحةً في `useSaveSetting`.
   */
  aiStatus: () => ['ai-status'] as const,
  commerce: () => ['commerce'] as const,
  /**
   * دفتر العُهد له جذر واحد؛ الاستلام والخصم وإضافة جهة قد تصل من البوت،
   * وإبطال الجذر يجعل الملخص والقائمة والجهة المختارة لقطةً واحدة بعد الحدث.
   */
  /**
   * المتابعة اليومية (قرار O-37) — جذرٌ واحد: ضغطةٌ في البوت تُبطل اليوم والسجلّ
   * والإعدادات معًا، فلا تعرض الشاشة يومًا غير الذي في تيليجرام.
   */
  dailyScope: () => ['daily'] as const,
  dailyToday: () => ['daily', 'today'] as const,
  dailyHistoryScope: () => ['daily', 'history'] as const,
  dailyHistory: (days: number) => ['daily', 'history', days] as const,
  dailySettings: () => ['daily', 'settings'] as const,
  apiTokens: () => ['api-tokens'] as const,
} as const;
